import React from 'react'
import { LOCAL_STORAGE_KEYS } from 'utils/constants'

type FunModeContextValue = {
  funModeEnabled: boolean
  setFunModeEnabled: (value: boolean) => void
  toggleFunMode: () => void
}

const FunModeContext = React.createContext<FunModeContextValue>({
  funModeEnabled: false,
  setFunModeEnabled: () => {},
  toggleFunMode: () => {},
})

/**
 * Combo clavier requis pour activer le FunMode.
 * Toutes les touches doivent être enfoncées simultanément.
 */
const FUN_COMBO_KEYS = ['f', 'u', 'n']

const FunModeProvider: React.FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const [funModeEnabled, setFunModeEnabled] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false

    try {
      const stored = window.localStorage.getItem(LOCAL_STORAGE_KEYS.FUN_MODE_ENABLED)
      return stored === 'true'
    } catch {
      return false
    }
  })

  const toggleFunMode = React.useCallback(() => {
    setFunModeEnabled((prev) => !prev)
  }, [])

  // Persist state in localStorage
  React.useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      window.localStorage.setItem(
        LOCAL_STORAGE_KEYS.FUN_MODE_ENABLED,
        funModeEnabled ? 'true' : 'false',
      )
    } catch {
      // ignore write errors (private mode, etc.)
    }
  }, [funModeEnabled])

  // Secret combo: press F + U + N simultaneously
  React.useEffect(() => {
    const pressedKeys = new Set<string>()

    const isTypingContext = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false
      const tag = target.tagName
      return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isTypingContext(event.target)) return

      pressedKeys.add(event.key.toLowerCase())

      const isComboActive = FUN_COMBO_KEYS.every((key) => pressedKeys.has(key))

      if (isComboActive) {
        toggleFunMode()
        pressedKeys.clear() // avoid retrigger while keys still pressed
      }
    }

    const handleKeyUp = (event: KeyboardEvent) => {
      pressedKeys.delete(event.key.toLowerCase())
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [toggleFunMode])

  const value = React.useMemo(
    () => ({ funModeEnabled, setFunModeEnabled, toggleFunMode }),
    [funModeEnabled, toggleFunMode],
  )

  return <FunModeContext.Provider value={value}>{children}</FunModeContext.Provider>
}

const useFunMode = () => React.useContext(FunModeContext)

export { FunModeProvider, useFunMode }
